/**
 * Registro de módulos — FUENTE DE VERDAD de "qué módulos existen" en la app,
 * no de "qué está activo". La activación/desactivación por distrito vive en
 * district_settings (key='enabled_modules') + is_module_enabled() (SQL) +
 * useEnabledModules() (frontend) — ver Fase 2.
 *
 * Este archivo NO se conecta todavía a ningún componente de nav/UI. Es solo
 * el catálogo, construido a partir de la auditoría de:
 *   - frontend/src/components/Layout.jsx        (nav inferior del pastor)
 *   - frontend/src/components/member/MemberNav.jsx (nav inferior del miembro)
 *   - frontend/src/pages/District.jsx            (LINKS_PASTORAL, LINKS_COMUNICACION,
 *                                                  LINKS_RECURSOS — 3 grids, no solo uno)
 *   - frontend/src/pages/Materials.jsx            (submenú de Materiales)
 *   - frontend/src/pages/Evangelism.jsx           (submenú de Evangelismo)
 *
 * Fuera de alcance a propósito: rutas /admin/* (AdminUsers, AdminOperations,
 * etc.) — esas ya están gateadas por rol (RequireAdmin en App.js) y no son un
 * concepto "por distrito", son siempre admin-only.
 *
 * `key` — identificador estable, snake_case, es el que se guardará dentro del
 *         JSONB de district_settings.enabled_modules (ej. { "visitas": false }).
 * `label` — nombre visible tal como aparece hoy en la UI.
 * `parent` — key del módulo padre si es un submódulo (Materiales/Evangelismo),
 *            null si es de primer nivel.
 * `locations` — dónde vive hoy en el código (archivo, componente, ruta).
 * `toggleable` — si un admin podrá activar/desactivar este módulo en la Fase 2
 *                de UI (SettingsModules.jsx). Los submódulos de Materiales/
 *                Evangelismo son `false`: se gatean en cascada por su padre,
 *                no individualmente. Módulos "estructurales" (Inicio, nav del
 *                miembro, etc.) también son `false` — no tiene sentido dejar
 *                que alguien se desactive Inicio a sí mismo.
 */

export const MODULES_REGISTRY = [
  // ── Nav pastor (Layout.jsx) ──────────────────────────────────────────────
  {
    key: "inicio",
    label: "Inicio",
    parent: null,
    toggleable: false,
    locations: [
      'Layout.jsx — BASE_TABS, tab "Inicio", ruta "/"',
      'Layout.jsx — tabs coordinador, tab "Inicio", ruta "/coordinator-dashboard"',
    ],
  },
  {
    key: "iglesias",
    label: "Iglesias",
    parent: null,
    toggleable: false,
    locations: [
      'Layout.jsx — BASE_TABS, tab "Iglesias", ruta "/churches"',
      'Layout.jsx — tabs coordinador, tab "Iglesia" (su propia iglesia), ruta "/churches/:id"',
    ],
  },
  {
    key: "visitas",
    label: "Visitas",
    parent: null,
    toggleable: false,
    locations: [
      'Layout.jsx — BASE_TABS, tab "Visitas", ruta "/visits"',
      'Layout.jsx — tabs coordinador, tab "Visitas", ruta "/visits"',
    ],
  },
  {
    key: "oracion",
    label: "Oración",
    parent: null,
    toggleable: false,
    locations: [
      'Layout.jsx — BASE_TABS, tab "Oración", ruta "/prayers"',
      'Layout.jsx — tabs coordinador, tab "Oración", ruta "/prayers"',
    ],
  },
  {
    key: "distrito",
    label: "Distrito",
    parent: null,
    toggleable: false,
    locations: [
      'Layout.jsx — BASE_TABS (slot DISTRICT_TAB), ruta "/district"',
      "pages/District.jsx — página completa del hub distrital",
    ],
  },
  {
    key: "familias",
    label: "Familias",
    parent: null,
    toggleable: false,
    locations: [
      'Layout.jsx — FAMILIES_TAB definido (ruta "/families") pero HOY NO SE USA: ' +
        "el slot null de BASE_TABS siempre se reemplaza por DISTRICT_TAB, nunca por " +
        "FAMILIES_TAB — es código muerto en el nav. La ruta /families sigue existiendo " +
        "y es alcanzable por otros links (ej. desde detalle de iglesia).",
    ],
  },
  {
    key: "notas",
    label: "Notas",
    parent: null,
    toggleable: false,
    locations: ['Layout.jsx — BASE_TABS, tab "Notas", ruta "/notes" (no aparece en nav de coordinador)'],
  },
  {
    key: "agenda",
    label: "Agenda",
    parent: null,
    toggleable: false,
    locations: ['Layout.jsx — tabs coordinador, tab "Agenda", ruta "/agenda" (no aparece en nav de pastor)'],
  },
  {
    key: "anuncios",
    label: "Anuncios",
    parent: null,
    toggleable: false,
    locations: [
      'Layout.jsx — tabs coordinador, tab "Anuncios", ruta "/announcements"',
      'District.jsx — LINKS_COMUNICACION, "Anuncios", ruta "/announcements"',
    ],
  },
  {
    key: "reto_semanal",
    label: "Reto Semanal",
    parent: null,
    toggleable: true,
    locations: [
      "pages/Challenges.jsx (listado) + pages/ChallengeForm.jsx (crear/editar)",
      'rutas "/challenges", "/challenges/new", "/challenges/:id/edit"',
      'District.jsx — LINKS_COMUNICACION, "Reto Semanal", ícono Trophy',
    ],
  },
  {
    key: "mensajes",
    label: "Mensajes",
    parent: null,
    toggleable: false,
    locations: [
      "pages/MemberMessages.jsx — bandeja del pastor",
      'ruta "/member-messages"',
      'District.jsx — LINKS_COMUNICACION, "Mensajes", sub: "De miembros al portal"',
      "Distinto de Anuncios: Mensajes es miembro → pastor, se origina en " +
        "MessageSheet.jsx dentro de MemberSettings.jsx (portal del miembro).",
    ],
  },

  // ── Grid "Recursos y administración" (District.jsx → LINKS_RECURSOS) ────
  {
    key: "predicaciones",
    label: "Predicaciones",
    parent: null,
    toggleable: true,
    locations: ['District.jsx — LINKS_RECURSOS, "Predicaciones", ruta "/preaching"'],
  },
  {
    key: "materiales",
    label: "Materiales",
    parent: null,
    toggleable: true,
    locations: [
      'District.jsx — LINKS_RECURSOS, "Materiales", ruta "/materials" (hub padre)',
    ],
  },
  {
    key: "evangelismo",
    label: "Evangelismo",
    parent: null,
    toggleable: true,
    locations: [
      'District.jsx — LINKS_RECURSOS, "Evangelismo", ruta "/evangelism" (hub padre)',
    ],
  },
  {
    key: "revision_mensual",
    label: "Revisión mensual",
    parent: null,
    toggleable: true,
    locations: ['District.jsx — LINKS_RECURSOS, "Revisión mensual", ruta "/administrative-review"'],
  },
  {
    key: "equipo_distrital",
    label: "Equipo Distrital",
    parent: null,
    toggleable: false,
    locations: ['District.jsx — LINKS_RECURSOS, "Equipo Distrital", ruta "/district/team"'],
  },
  {
    key: "reportes_distritales",
    label: "Reportes Distritales",
    parent: null,
    toggleable: false,
    locations: ['District.jsx — LINKS_RECURSOS, "Reportes Distritales", ruta "/district/reports"'],
  },

  // ── Submenú de Materiales (Materials.jsx → MODULES) ──────────────────────
  {
    key: "materiales_suscripciones",
    label: "Suscripciones",
    parent: "materiales",
    toggleable: false,
    locations: ['Materials.jsx — MODULES, "Suscripciones", ruta "/materials/subscriptions"'],
  },
  {
    key: "materiales_distribuciones",
    label: "Distribuciones",
    parent: "materiales",
    toggleable: false,
    locations: ['Materials.jsx — MODULES, "Distribuciones", ruta "/materials/distributions"'],
  },
  {
    key: "materiales_inventario",
    label: "Inventario",
    parent: "materiales",
    toggleable: false,
    locations: ['Materials.jsx — MODULES, "Inventario", ruta "/materials/inventory"'],
  },
  {
    key: "materiales_finanzas",
    label: "Finanzas",
    parent: "materiales",
    toggleable: false,
    locations: ['Materials.jsx — MODULES, "Finanzas", ruta "/materials/dashboard"'],
  },
  {
    key: "gastos",
    label: "Gastos",
    parent: "materiales",
    toggleable: false,
    locations: [
      'Materials.jsx — MODULES, "Gastos", ruta "/district-expenses" ' +
        "(vive dentro del submenú de Materiales en la UI, pero es su propio módulo " +
        "de datos — district_expenses — no depende de materials)",
    ],
  },

  // ── Submenú de Evangelismo (Evangelism.jsx → MODULES) ────────────────────
  {
    key: "evangelismo_grupos",
    label: "Grupos pequeños",
    parent: "evangelismo",
    toggleable: false,
    locations: ['Evangelism.jsx — MODULES (key: "groups"), ruta "/evangelism/groups"'],
  },
  {
    key: "evangelismo_estudios",
    label: "Estudios bíblicos",
    parent: "evangelismo",
    toggleable: false,
    locations: ['Evangelism.jsx — MODULES (key: "studies"), ruta "/evangelism/bible-studies"'],
  },
  {
    key: "bautismos",
    label: "Bautismos",
    parent: "evangelismo",
    toggleable: false,
    locations: [
      'Evangelism.jsx — MODULES (key: "baptisms"), ruta "/baptisms" ' +
        "(vive dentro del submenú de Evangelismo en la UI, pero es su propio módulo " +
        "de datos — baptism_records — no depende de evangelism)",
    ],
  },

  // ── Nav miembro (MemberNav.jsx → TABS) ───────────────────────────────────
  // Incluidos aunque hoy sabemos que quedarán fuera del alcance de la
  // desactivación (visitas/familias/finanzas/notas/bautismos son pastor-side):
  // el registro es "qué existe", no "qué se puede desactivar".
  {
    key: "miembro_inicio",
    label: "Inicio (portal miembro)",
    parent: null,
    toggleable: false,
    locations: ['MemberNav.jsx — TABS, "Inicio", ruta "/member"'],
  },
  {
    key: "miembro_iglesia",
    label: "Iglesia (portal miembro)",
    parent: null,
    toggleable: false,
    locations: ['MemberNav.jsx — TABS, "Iglesia", ruta "/member/iglesia"'],
  },
  {
    key: "miembro_espiritual",
    label: "Espiritual (portal miembro)",
    parent: null,
    toggleable: false,
    locations: ['MemberNav.jsx — TABS, "Espiritual", ruta "/member/espiritual"'],
  },
  {
    key: "miembro_vida",
    label: "Vida (portal miembro)",
    parent: null,
    toggleable: false,
    locations: ['MemberNav.jsx — TABS, "Vida", ruta "/member/vida"'],
  },
  {
    key: "miembro_comunidad",
    label: "Comunidad (portal miembro)",
    parent: null,
    toggleable: false,
    locations: ['MemberNav.jsx — TABS, "Comunidad", ruta "/member/comunidad"'],
  },
];

/** Lookup rápido por key. */
export const MODULES_BY_KEY = Object.fromEntries(
  MODULES_REGISTRY.map((m) => [m.key, m])
);

/** Solo los módulos de primer nivel (sin padre) — útil para listas tipo "activar/desactivar". */
export const TOP_LEVEL_MODULES = MODULES_REGISTRY.filter((m) => m.parent === null);

/** Solo los módulos activables/desactivables por distrito — lo que consume SettingsModules.jsx. */
export const TOGGLEABLE_MODULES = MODULES_REGISTRY.filter((m) => m.toggleable === true);

/** Submódulos de un módulo padre dado. */
export function getSubmodules(parentKey) {
  return MODULES_REGISTRY.filter((m) => m.parent === parentKey);
}
